/**
 * The ad slot: when one appears, and what stands in for it until there is one.
 *
 * Two things that are deliberately separate. `AdPolicy` is arithmetic over the
 * interaction stream and knows nothing about the DOM, which is what makes the
 * rule -- one ad every so many actions, and not straight after the last one was
 * closed -- something a test can state rather than something only a person
 * watching the screen can check. `placeholderHtml` is the creative, and it is
 * the only part a real provider replaces.
 *
 * The slot is a strip at the bottom of the screen with a close control, and
 * closing it is final until the counter comes round again. It never covers the
 * map's own controls: on a narrow screen it docks above the bottom bar and the
 * sheets dock above it, which is a stylesheet decision like every other part of
 * this layout.
 */

import { escapeHtml } from "./format.ts";

/** Actions before the first ad of a session. */
export const FIRST_AFTER = 8;

/** Actions between one ad and the next. */
export const EVERY = 15;

/** Quiet time after a dismissal, however many actions fall inside it. */
export const COOLDOWN_MS = 45_000;

export interface AdPolicyOptions {
  firstAfter?: number;
  every?: number;
  cooldownMs?: number;
}

/**
 * Whether it is time to show an ad.
 *
 * The counter is not reset when the cooldown refuses a turn: it stays at the
 * threshold, so a reader who dismissed one and kept working sees the next one
 * on their first action after the quiet period rather than after another full
 * fifteen. That is the difference between an interval and a punishment for
 * closing it.
 */
export class AdPolicy {
  private readonly firstAfter: number;
  private readonly every: number;
  private readonly cooldownMs: number;
  private actions = 0;
  private shown = 0;
  private open = false;
  private closedAt = -Infinity;

  constructor(options: AdPolicyOptions = {}) {
    this.firstAfter = options.firstAfter ?? FIRST_AFTER;
    this.every = options.every ?? EVERY;
    this.cooldownMs = options.cooldownMs ?? COOLDOWN_MS;
  }

  /** How many actions this turn needs. */
  private threshold(): number {
    return this.shown === 0 ? this.firstAfter : this.every;
  }

  /**
   * Count one action, and say whether it earned an ad. True is the caller's
   * instruction to fill the slot, and is returned exactly once per ad.
   */
  note(now = Date.now()): boolean {
    this.actions += 1;
    if (this.open) return false;
    if (this.actions < this.threshold()) return false;
    if (now - this.closedAt < this.cooldownMs) return false;
    this.actions = 0;
    this.shown += 1;
    this.open = true;
    return true;
  }

  /** The reader closed it. */
  dismiss(now = Date.now()): void {
    if (!this.open) return;
    this.open = false;
    this.closedAt = now;
  }

  get isOpen(): boolean {
    return this.open;
  }

  /** For the console handle, and for a test that wants to say why. */
  get state() {
    return { actions: this.actions, needed: this.threshold(), shown: this.shown, open: this.open };
  }
}

/**
 * What fills the slot.
 *
 * A labelled box of the size the real unit will be, so the layout it has to
 * live in is the layout it was designed against. Swapping in a provider is
 * replacing this function -- the slot, the close control and the policy do not
 * know what is inside it.
 */
export function placeholderHtml(): string {
  const note = "Ad slot 320 x 50";
  return `<div class="strip-placeholder">${escapeHtml(note)}</div>`;
}

/**
 * What fills the slot when a blocker has eaten the ad.
 *
 * The slot still opens on the same interval, so the layout and the counters
 * behave identically either way -- and a reader running a blocker gets a joke
 * instead of an empty box.
 */
export function blockedHtml(): string {
  return `<div class="strip-blocked">` +
    `<b>Ad blocked</b>` +
    `<span>Consider allowing ads to support the website.</span>` +
    `</div>`;
}

/**
 * The class names blockers hide on sight. A bait element wearing them that
 * comes back with no size, or not at all, means a cosmetic filter is running.
 */
const BAIT_CLASSES = "adsbox ad-banner adsbygoogle ad-unit pub_300x250 text-ad textads";

/** Long enough for a blocker that removes elements rather than styling them. */
const BAIT_WAIT_MS = 150;

/**
 * Whether an ad blocker is running, asked once per page.
 *
 * A bait element rather than a request for a known ad script: there is no
 * provider yet, and a request to one would be a third party hearing about every
 * visit in order to learn something the page can find out on its own. When a
 * provider is chosen, its script failing to load is the second, stronger signal
 * and belongs beside this one.
 *
 * The slot itself is named `strip`, not `ad`, for the same reason the bait is
 * named `ad`: the generic filter lists hide anything that says so, and the
 * banner has to survive the blocker it is about.
 */
export function detectAdBlocker(): Promise<boolean> {
  return new Promise((resolve) => {
    const bait = document.createElement("div");
    bait.className = BAIT_CLASSES;
    bait.setAttribute("aria-hidden", "true");
    bait.style.cssText = "position:absolute;left:-10000px;top:-10000px;width:1px;height:1px;";
    bait.innerHTML = "&nbsp;";
    document.body.appendChild(bait);
    window.setTimeout(() => {
      const style = bait.isConnected ? window.getComputedStyle(bait) : null;
      const blocked = !style || bait.offsetHeight === 0 || style.display === "none" ||
        style.visibility === "hidden";
      bait.remove();
      resolve(blocked);
    }, BAIT_WAIT_MS);
  });
}
